/**
 * Debounced search for async Select — fires a search request after 500ms
 * of inactivity. Skips the empty initial call (initial options are fetched
 * by usePaginatedFetch). Cancels pending searches on unmount.
 *
 * `isFirstLoad`, `loadOptions`, and `makeRequest` are read through refs so
 * `onSearchChange` (and therefore the debounced wrapper) has a stable
 * identity across renders. Without this, a render that updates any of those
 * three would recreate the debounced fn and the cleanup `useEffect` would
 * `.cancel()` any pending timer — silently dropping keystrokes the user
 * typed before the initial load finished (LTDS-46).
 */
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { UseDebouncedSearchParams } from './types';

export const useDebouncedSearch = <T = undefined>({
  loadOptions,
  isFirstLoad,
  makeRequest,
}: UseDebouncedSearchParams<T>): ReturnType<typeof debounce> => {
  const isFirstLoadRef = useRef(isFirstLoad);
  const loadOptionsRef = useRef(loadOptions);
  const makeRequestRef = useRef(makeRequest);
  isFirstLoadRef.current = isFirstLoad;
  loadOptionsRef.current = loadOptions;
  makeRequestRef.current = makeRequest;

  const onSearchChange = useCallback(async (searchTerm?: string) => {
    if (isFirstLoadRef.current) {
      return;
    }
    await makeRequestRef.current({
      loadOptions: loadOptionsRef.current,
      mode: 'search',
      value: searchTerm,
    });
  }, []);

  const debouncedSearchChange = useMemo(
    () => debounce(onSearchChange, 500),
    [onSearchChange],
  );

  // Cancel pending debounced search when isFirstLoad resets to true (popover
  // closed). Without this, a search scheduled just before close would still
  // be in flight; today that's harmlessly suppressed by the ref-read, but
  // relying on that is a coincidence — cancel explicitly so the fix doesn't
  // depend on usePaginatedFetch.resetState's behavior.
  useEffect(() => {
    if (isFirstLoad) {
      debouncedSearchChange.cancel();
    }
  }, [isFirstLoad, debouncedSearchChange]);

  // Cancel pending debounced search on unmount
  useEffect(() => {
    return () => debouncedSearchChange.cancel();
  }, [debouncedSearchChange]);

  return debouncedSearchChange;
};
